'use client';

import { Box, darken, lighten, ThemeProvider } from '@mui/material';
import { cloneDeep, defaultsDeep, set } from 'lodash';
import { PropsWithChildren, useMemo } from 'react';
import { getBrokerFromHostName } from '../services/broker';
import { useBranding } from '../store/useBranding';
import { lightTheme } from '../themes';
import { generateTheme, MTFTheme } from '../utils/theme';

const CustomThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { data } = useBranding(getBrokerFromHostName());

  const theme = useMemo(() => {
    if (!data) return lightTheme;
    const brandTheme =
      data?.metaData?.intradayTheme || data?.metaData?.theme || lightTheme;
    const themeCopy = cloneDeep(lightTheme);
    const customColors: MTFTheme = defaultsDeep(
      brandTheme,
      generateTheme(brandTheme)
    );

    set(themeCopy, 'palette.primary.main', customColors.global.accentColor);
    set(
      themeCopy,
      'palette.primary.light',
      lighten(customColors.global.accentColor, 0.8)
    );
    set(
      themeCopy,
      'palette.primary.dark',
      darken(customColors.global.accentColor, 0.1)
    );
    set(themeCopy, 'palette.custom', {
      ...themeCopy.palette.custom,
      ...customColors,
    });

    const background = {
      default: customColors.global.backgroundColor,
    };
    const text = {
      primary: customColors.global.textColor,
    };

    set(themeCopy, 'palette.background', background);
    set(themeCopy, 'palette.text', text);

    return themeCopy;
  }, [data]);

  return (
    <ThemeProvider theme={theme}>
      <Box>{children}</Box>
    </ThemeProvider>
  );
};

export default CustomThemeProvider;
